<template>
  <v-container>
    <v-row
      justify="center"
      align="center"
      class="pa-2 text-center"
      v-if="!loading"
    >
      <v-col cols="12">
        <h6 class="text-h5">
          {{ $_t('Thank you for completing the questionnaires') }}
        </h6>
        <div class="text-subtitle-1">
          {{
            $_t(
              'You have previously received a confirmation email which contains a summary of the requirements for your exam including times and other appointment related information.'
            )
          }}
        </div>
      </v-col>
      <v-col cols="12">
        <div class="text-subtitle-1">
          {{ $_t('Your appointment details are below.') }}
          <br />{{
            $_t(
              'Please note that you are required to arrive in the clinic ahead of your appointment time. The exact required arrival time was indicated in the confirmation email and will also be stated in the reminder SMS message.'
            )
          }}
        </div>
        <div class="text-subtitle-1 red--text" v-if="showFaceMaskInfo">
          {{
            $_t(
              'Please note that all patients are required to wear a face mask at all times while in the clinic. Refusal to do so will result in the procedure being cancelled.'
            )
          }}
        </div>
        <div>
          <appointment-procedures-card
            v-if="appointment"
            :appointment="appointment"
          />
        </div>
      </v-col>
      <v-col cols="12" v-if="config.showThankYouPdf && isAppointmentConfirmed">
        <div v-if="docLoading">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
        </div>
        <div v-else>
          <div style="width: 100%;" v-if="blobURL">
            <pdf-viewer :in-dialog="true" :src="blobURL"></pdf-viewer>
          </div>
          <v-alert v-else type="warning">{{
            $_t('Attached document could not be loaded')
          }}</v-alert>
        </div>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12" class="text-center">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { defineComponent, computed, onMounted, ref } from '@vue/composition-api'
import AppointmentProceduresCard from '@components/appointment/cards/AppointmentProceduresCard.vue'
import config from '@config'
import PdfViewer from '../common/PdfViewer.vue'

export default defineComponent({
  name: 'AppointmentThankYou',
  components: { AppointmentProceduresCard, PdfViewer },
  props: {
    options: { type: Object, default: {} }
  },
  setup(props, ctx) {
    const getOptions = computed(() => {
      return props.options
    })

    const showFaceMaskInfo = computed(() => {
      return !config.hideCovidFaceMaskInfo
    })
    const appointment = computed(
      () => ctx.root.$store.getters['appointment/appointment']
    )

    const isAppointmentConfirmed = computed(() => {
      return appointment.value?.status?.isConfirmed
    })

    const nextPage = page => {
      ctx.emit('page', page)
    }

    const loading = ref(true)
    const docLoading = ref(false)
    const count = ref(0)
    const blobURL = ref(null)

    const init = () => {
      ctx.root.$store.commit('app/setAppBarTitle', 'Thank you')
      ctx.root.$store.commit('app/setAppBarButton1', null)
      ctx.root.$store.commit('app/setAppBarButton2', null)
      ctx.root.$store.dispatch('app/appBarShow')
      loading.value = false
      ctx.root.$emit('scrollToTop')
    }

    const getAppointmentLetter = () => {
      count.value++
      docLoading.value = true
      if (count.value > 10) {
        docLoading.value = false
        return
      }
      ctx.root.$_rest.get(
        `/patients/${ctx.root.$store.getters['app/currentUserId']}/appointments/${appointment.value.id}/appointment-letter`,
        {},
        response => {
          if (response.status === 202) {
            setTimeout(() => {
              getAppointmentLetter()
            }, 1000)
            return
          } else if (response.status === 200) {
            response.blob().then(blob => {
              blobURL.value = window.URL.createObjectURL(blob)
              docLoading.value = false
            })
          }
        },
        error => {
          setTimeout(() => {
            getAppointmentLetter()
          }, 1000)
        },
        {
          handleResponse: false
        }
      )
    }

    if (config.showThankYouPdf && appointment.value) {
      getAppointmentLetter()
    }

    init()

    return {
      getOptions,
      nextPage,
      init,
      appointment,
      showFaceMaskInfo,
      loading,
      config,
      getAppointmentLetter,
      docLoading,
      blobURL,
      isAppointmentConfirmed
    }
  }
})
</script>
